import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { trackNewsletterSubscribe } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { subscribeNewsletterFn } from '@/server/functions/newsletter';

type NewsletterSignupProps = {
    className?: string;
    /** Compact layout for sidebar rails. */
    compact?: boolean;
    /** Stretch to fill parent height; actions align to the bottom. */
    stretch?: boolean;
    /** Unique input id when multiple forms are on the page. */
    id?: string;
};

export function NewsletterSignup({
    className,
    compact = false,
    stretch = false,
    id = 'newsletter-email',
}: NewsletterSignupProps) {
    const [email, setEmail] = useState('');
    const [success, setSuccess] = useState<string | null>(null);

    const mutation = useMutation({
        mutationFn: async (value: string) => {
            return subscribeNewsletterFn({ data: { email: value } });
        },
        onSuccess: (result) => {
            setSuccess(result.message);
            setEmail('');
            trackNewsletterSubscribe();
        },
    });

    const errorMessage =
        mutation.error instanceof Error
            ? mutation.error.message
            : mutation.error &&
                typeof mutation.error === 'object' &&
                'message' in mutation.error
              ? String((mutation.error as { message: unknown }).message)
              : null;

    const heading = (
        <>
            <p className={cn('text-white', compact ? 'text-sm' : 'text-sm')}>
                הרשמה לניוזלטר
            </p>
            <p
                className={cn(
                    'mt-1.5 leading-relaxed text-white/55',
                    compact ? 'text-sm' : 'text-sm',
                )}
            >
                קבלו עדכונים ישירות למייל. נשלח קישור אימות - רק אחרי אימות
                תצטרפו לרשימה.
            </p>
        </>
    );

    const actions = (
        <>
            {success ? (
                <p
                    className={cn(
                        'text-sm leading-relaxed text-brand',
                        !stretch && 'mt-4',
                    )}
                    role="status"
                >
                    {success}
                </p>
            ) : (
                <form
                    className={cn(
                        'flex items-center gap-2',
                        !stretch && 'mt-4',
                    )}
                    onSubmit={(event) => {
                        event.preventDefault();
                        const trimmed = email.trim();
                        if (!trimmed || mutation.isPending) return;
                        mutation.mutate(trimmed);
                    }}
                >
                    <label className="sr-only" htmlFor={id}>
                        כתובת אימייל
                    </label>
                    <input
                        id={id}
                        type="email"
                        name="email"
                        autoComplete="email"
                        required
                        dir="ltr"
                        placeholder="me@example.com"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        disabled={mutation.isPending}
                        className="h-10 min-h-0 min-w-0 flex-1 appearance-none border border-white/15 bg-white/[0.03] px-3 text-sm leading-none text-white outline-none placeholder:text-white/55 focus:border-white/30 disabled:opacity-60"
                    />
                    <button
                        type="submit"
                        disabled={mutation.isPending}
                        className="inline-flex h-10 shrink-0 items-center justify-center border border-white/15 bg-white/[0.03] px-4 text-sm leading-none text-white transition-colors hover:border-white/30 hover:bg-white/[0.06] disabled:opacity-60"
                    >
                        {mutation.isPending ? 'שולחים…' : 'הירשמו'}
                    </button>
                </form>
            )}

            {errorMessage && !success && (
                <p className="mt-3 text-sm text-red-400" role="alert">
                    {errorMessage}
                </p>
            )}
        </>
    );

    if (stretch) {
        return (
            <div
                className={cn(
                    'flex h-full flex-col justify-between gap-4',
                    className,
                )}
            >
                <div>{heading}</div>
                <div>{actions}</div>
            </div>
        );
    }

    return (
        <div className={cn(className)}>
            {heading}
            {actions}
        </div>
    );
}
